import type { Language } from '../contexts/I18nContext';

class TranslationService {
  private translations: Record<string, any> = {};
  private currentLanguage: Language = 'ar';

  async loadTranslations(language: Language): Promise<void> {
    try {
      const translationModules = await Promise.allSettled([
        import(`../locales/${language}/common.json`),
        import(`../locales/${language}/navigation.json`),
        import(`../locales/${language}/theme.json`),
        import(`../locales/${language}/language.json`),
        import(`../locales/${language}/auth.json`),
        import(`../locales/${language}/dashboard.json`),
        import(`../locales/${language}/holidays.json`),
        import(`../locales/${language}/users.json`),
        import(`../locales/${language}/reports.json`),
        import(`../locales/${language}/settings.json`),
        import(`../locales/${language}/forms.json`),
        import(`../locales/${language}/errors.json`),
        import(`../locales/${language}/notifications.json`),
        import(`../locales/${language}/tables.json`),
      ]);

      const moduleNames = [
        'common', 'navigation', 'theme', 'language', 'auth', 'dashboard',
        'holidays', 'users', 'reports', 'settings', 'forms', 'errors',
        'notifications', 'tables'
      ];

      this.translations[language] = {};

      translationModules.forEach((result, index) => {
        const moduleName = moduleNames[index];
        if (result.status === 'fulfilled') {
          this.translations[language][moduleName] = result.value.default;
        } else {
          console.warn(`Failed to load ${moduleName} translations for ${language}:`, result.reason);
          // Use empty object as fallback
          this.translations[language][moduleName] = {};
        }
      });
      
      this.currentLanguage = language;
    } catch (error) {
      console.warn(`Failed to load translations for ${language}:`, error);
      // Initialize with empty translations to prevent crashes
      if (!this.translations[language]) {
        this.translations[language] = {
          common: {}, navigation: {}, theme: {}, language: {}, auth: {},
          dashboard: {}, holidays: {}, users: {}, reports: {}, settings: {},
          forms: {}, errors: {}, notifications: {}, tables: {}
        };
      }
      // Fallback to Arabic if available
      if (language !== 'ar' && this.translations.ar) {
        this.currentLanguage = 'ar';
      } else {
        this.currentLanguage = language;
      }
    }
  }

  translate(key: string, params?: Record<string, string>): string {
    const keys = key.split('.');
    let value = this.translations[this.currentLanguage];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English
        value = this.translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if translation not found
          }
        }
        break;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    // Simple parameter replacement
    if (params) {
      return Object.entries(params).reduce(
        (str, [param, replacement]) => str.replace(new RegExp(`\\{${param}\\}`, 'g'), replacement),
        value
      );
    }

    return value;
  }

  getCurrentLanguage(): Language {
    return this.currentLanguage;
  }

  isRTL(): boolean {
    return this.currentLanguage === 'ar';
  }
}

export const translationService = new TranslationService();